import { type ApiSchemas, call } from '@rental/contracts/client'
import { format } from '@rental/i18n'
import { Button, Checkbox, cn, Field, Notice } from '@rental/ui'
import { useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useEffect, useId, useRef, useState } from 'react'
import { useAccess } from '../access-context'
import { api } from '../api'
import { ReasonList, SubPanel } from '../components/SubPanel'
import {
  FIELD,
  FIELD_ROW,
  FieldPair,
  PAIRED_FIELD,
  ProblemNotice,
  SHORT_FIELD,
  StaffInput,
} from '../components/ui'
import { noImplicitSubmit } from '../forms'
import { useLocale } from '../i18n'
import { problemOf, type StaffProblem } from '../problems'
import type { CorrectionOutcome } from './CustomerCorrectionForm'
import {
  type Based,
  type CategoryDraft,
  invalidLicenceFields,
  LICENCE_FIELDS,
  type LicenceDraft,
  type LicenceField,
  licenceCorrection,
  licenceDraftOf,
  rebase,
  refusedFields,
} from './correction'
import { customerQueryKey, loadCustomer } from './queries'

type Licence = ApiSchemas['CustomerDetail']['licences'][number]

/**
 * S8 (GDPR art. 16): one of a customer's licences put right, as the
 * customer's details are: from the licence as read, only what changed, the
 * version checked by the server. A licence erased meanwhile is gone: the
 * form says so and closes.
 */
export function LicenceCorrectionForm({
  customerId,
  licence,
  onDone,
  onCancel,
}: {
  customerId: string
  licence: Licence
  onDone: (outcome: CorrectionOutcome) => void
  onCancel: () => void
}) {
  const { messages: m } = useLocale()
  const copy = m.customer.licenceCorrection
  const words = m.customer.correction
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const titleId = useId()
  const reasonsId = useId()
  const firstField = useRef<HTMLInputElement>(null)
  const rereadAction = useRef<HTMLDivElement>(null)
  const saveAction = useRef<HTMLDivElement>(null)
  const focusNext = useRef<'reread' | 'save' | null>(null)
  const [base, setBase] = useState<Based<LicenceDraft>>(() => licenceDraftOf(licence))
  const [draft, setDraft] = useState<LicenceDraft>(() => licenceDraftOf(licence).values)
  const [busy, setBusy] = useState<'saving' | 'reading' | null>(null)
  const [problem, setProblem] = useState<StaffProblem | null>(null)
  const [refused, setRefused] = useState<LicenceField[]>([])
  const [conflict, setConflict] = useState(false)
  const [reread, setReread] = useState<{ kept: LicenceField[]; clashed: LicenceField[] } | null>(
    null,
  )

  useEffect(() => {
    firstField.current?.focus()
  }, [])

  useEffect(() => {
    const next = focusNext.current
    if (!next) return
    focusNext.current = null
    const button = (next === 'reread' ? rereadAction : saveAction).current?.querySelector('button')
    if (button && !button.disabled) button.focus()
    else firstField.current?.focus()
  })

  const invalid = invalidLicenceFields(draft)
  const { changed } = licenceCorrection(base, draft)
  const reasons = busy
    ? []
    : conflict
      ? [words.why.conflict]
      : changed.length === 0
        ? [words.why.nothing]
        : invalid.includes('categories')
          ? [copy.why.categories]
          : invalid.length > 0
            ? [words.why.invalid]
            : []
  const names = (fields: readonly LicenceField[]) =>
    fields.map((field) => copy.fields[field]).join(', ')
  const errorOf = (field: LicenceField) =>
    invalid.includes(field) || refused.includes(field) ? words.invalid : null

  function set<K extends keyof LicenceDraft>(field: K, value: LicenceDraft[K]) {
    setDraft((current) => ({ ...current, [field]: value }))
    setRefused((current) => current.filter((name) => name !== field))
  }

  function setCategory(index: number, change: Partial<CategoryDraft>) {
    set(
      'categories',
      draft.categories.map((row, at) => (at === index ? { ...row, ...change } : row)),
    )
  }

  async function readAgain() {
    setBusy('reading')
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: customerQueryKey(customerId),
        queryFn: () => loadCustomer(customerId),
        staleTime: 0,
      })
      const now = fresh.licences.find((row) => row.id === licence.id)
      if (fresh.customer.anonymisedAt !== null || !now) {
        onDone({ kind: 'gone' })
        return
      }
      const next = rebase(base, draft, licenceDraftOf(now))
      setBase(next.base)
      setDraft(next.draft)
      setConflict(false)
      setReread({ kept: next.kept, clashed: next.clashed })
      focusNext.current = 'save'
    } catch (error) {
      setProblem(problemOf(error))
    } finally {
      setBusy(null)
    }
  }

  async function save() {
    if (busy || reasons.length > 0) return
    const { body } = licenceCorrection(base, draft)
    setProblem(null)
    setRefused([])
    setReread(null)
    setBusy('saving')
    const result = await call(
      api.PATCH('/v1/staff/customers/{customerId}/licences/{licenceId}', {
        params: { path: { customerId, licenceId: licence.id } },
        body,
      }),
    )
    setBusy(null)
    if (result.ok) {
      onDone(
        result.data.changed.length === 0
          ? { kind: 'unchanged' }
          : { kind: 'saved', fields: result.data.changed.map((field) => copy.fields[field]) },
      )
      return
    }
    if (takeOver(result.problem)) return
    const code = result.problem.kind === 'api' ? result.problem.code : null
    if (code === 'version_conflict') {
      setConflict(true)
      focusNext.current = 'reread'
      return
    }
    // Erased with its customer meanwhile (404), or the customer anonymised: nothing left to correct.
    if (code === 'customer_anonymised' || code === 'not_found') {
      onDone({ kind: 'gone' })
      return
    }
    setRefused(refusedFields(result.problem, LICENCE_FIELDS))
    setProblem(result.problem)
  }

  return (
    <form
      onSubmit={(event: FormEvent) => {
        event.preventDefault()
        void save()
      }}
      onKeyDown={noImplicitSubmit}
      aria-labelledby={titleId}
      noValidate
    >
      <SubPanel title={<span id={titleId}>{copy.title}</span>}>
        <div className={FIELD_ROW}>
          <Field className={FIELD} label={copy.number} required error={errorOf('number')}>
            {(control) => (
              <StaffInput
                {...control}
                ref={firstField}
                autoComplete="off"
                maxLength={40}
                value={draft.number}
                onChange={(event) => set('number', event.target.value)}
              />
            )}
          </Field>
          <Field
            className={SHORT_FIELD}
            label={copy.issuingCountry}
            required
            error={errorOf('issuingCountry')}
          >
            {(control) => (
              <StaffInput
                {...control}
                autoComplete="off"
                maxLength={2}
                autoCapitalize="characters"
                value={draft.issuingCountry}
                onChange={(event) => set('issuingCountry', event.target.value.toUpperCase())}
              />
            )}
          </Field>
          <Field
            className={FIELD}
            label={copy.expiresOn}
            optionalLabel={words.optional}
            error={errorOf('expiresOn')}
          >
            {(control) => (
              <StaffInput
                {...control}
                type="date"
                value={draft.expiresOn}
                onChange={(event) => set('expiresOn', event.target.value)}
              />
            )}
          </Field>
        </div>
        <fieldset className="flex flex-col gap-3">
          <legend className="text-sm font-medium text-text">{copy.categories}</legend>
          {draft.categories.map((row, index) => (
            // Rows have no identity but their place: a removed row takes its fields with it.
            // biome-ignore lint/suspicious/noArrayIndexKey: rows are positions in the form
            <div key={index} className={cn(FIELD_ROW, 'items-end')}>
              <FieldPair>
                <Field className={PAIRED_FIELD} label={copy.category}>
                  {(control) => (
                    <StaffInput
                      {...control}
                      autoComplete="off"
                      maxLength={10}
                      autoCapitalize="characters"
                      value={row.code}
                      onChange={(event) =>
                        setCategory(index, { code: event.target.value.toUpperCase() })
                      }
                    />
                  )}
                </Field>
                <Field
                  className={PAIRED_FIELD}
                  label={copy.firstIssuedOn}
                  optionalLabel={words.optional}
                >
                  {(control) => (
                    <StaffInput
                      {...control}
                      type="date"
                      value={row.firstIssuedOn}
                      onChange={(event) =>
                        setCategory(index, { firstIssuedOn: event.target.value })
                      }
                    />
                  )}
                </Field>
              </FieldPair>
              {draft.categories.length > 1 ? (
                <Button
                  variant="outline"
                  className="rounded-sm"
                  aria-label={format(copy.removeCategoryLabel, { code: row.code || '—' })}
                  onClick={() =>
                    set(
                      'categories',
                      draft.categories.filter((_, at) => at !== index),
                    )
                  }
                >
                  {copy.removeCategory}
                </Button>
              ) : null}
            </div>
          ))}
          {errorOf('categories') ? (
            <p className="text-sm font-medium text-danger">{copy.why.categories}</p>
          ) : null}
          <div>
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() =>
                set('categories', [...draft.categories, { code: '', firstIssuedOn: '' }])
              }
            >
              {copy.addCategory}
            </Button>
          </div>
        </fieldset>
        <Checkbox
          label={copy.hasIdp}
          checked={draft.hasIdp}
          onChange={(event) => set('hasIdp', event.target.checked)}
        />
        <p className="text-sm text-text-muted">{words.keepsRecords}</p>
        {conflict ? (
          <>
            <Notice tone="warning" live="assertive">
              <p>{words.conflict}</p>
            </Notice>
            <div ref={rereadAction}>
              <Button
                variant="outline"
                className="rounded-sm"
                busy={busy === 'reading'}
                onClick={() => void readAgain()}
              >
                {words.reread}
              </Button>
            </div>
          </>
        ) : null}
        {reread ? (
          <Notice tone={reread.clashed.length > 0 ? 'warning' : 'info'} live="polite">
            <p>
              {reread.kept.length > 0
                ? format(words.rereadKept, { fields: names(reread.kept) })
                : words.rereadNone}
            </p>
            {reread.clashed.length > 0 ? (
              <p>{format(words.clashed, { fields: names(reread.clashed) })}</p>
            ) : null}
          </Notice>
        ) : null}
        <ProblemNotice problem={problem} />
        <div ref={saveAction} className="flex flex-wrap gap-3">
          <Button
            type="submit"
            variant="staff"
            busy={busy === 'saving'}
            disabled={reasons.length > 0}
            aria-describedby={reasons.length > 0 ? reasonsId : undefined}
          >
            {words.save}
          </Button>
          <Button
            variant="outline"
            className="rounded-sm"
            disabled={busy === 'saving'}
            onClick={onCancel}
          >
            {words.cancel}
          </Button>
        </div>
        <ReasonList id={reasonsId} reasons={reasons} />
      </SubPanel>
    </form>
  )
}
